/**
 * ============================================================================
 * Section items — the polls, prompts and final assessment a class runs on
 * ============================================================================
 * A section is more than hours in a chair. This is where the coordinator
 * writes what the room will be asked: a poll, a true/false, an order-of-
 * priority exercise, an open "what are your concerns?", and the end-of-session
 * assessment that decides completion (Brian, 2026-09-12).
 *
 * THE WHOLE LIST IS SAVED AT ONCE. Editing five items is one act, so it is one
 * request; the server replaces the list and reads it back. A coordinator never
 * sees a half-saved list.
 *
 * ITEMS LIVE ON THE PUBLISHED VERSION, because a session is asked exactly the
 * items frozen with the version it runs against. Until the section is
 * published this panel says so rather than offering a form that cannot save.
 *
 * The completion check is a flag on each item, not "the last one": a final
 * assessment is often several questions and the instructor may push a
 * discussion prompt after it.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import ErrorBanner from './ErrorBanner.jsx';
import Field from './Field.jsx';
import Select from './Select.jsx';

const TYPE_OPTIONS = [
  { value: 'multiple_choice', label: 'Multiple choice' },
  { value: 'true_false', label: 'True / false' },
  { value: 'sequencing', label: 'Order of priority' },
  { value: 'free_text', label: 'Open text' },
];

const TYPE_LABEL = Object.fromEntries(TYPE_OPTIONS.map((t) => [t.value, t.label]));

function blank(type = 'multiple_choice') {
  return {
    id: null,
    type,
    stem: '',
    options: type === 'multiple_choice' || type === 'sequencing' ? ['', ''] : [],
    correct: type === 'true_false' ? 'True' : null,
    rationale: '',
    is_completion_check: false,
    // client-only key so React can track unsaved rows
    _key: crypto.randomUUID(),
  };
}

export function SectionItemsPanel({ classId, sectionStatus }) {
  const [state, setState] = useState(undefined); // undefined loading · null failed · object loaded
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(null); // index being edited, or null

  const load = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/v1/classes/${classId}/questions`);
      setState(data);
      setItems((data.items ?? []).map((i) => ({ ...i, _key: i.id })));
      setDirty(false);
    } catch (err) {
      setState(null);
      setError(err);
    }
  }, [classId]);

  useEffect(() => {
    load();
  }, [load, sectionStatus]);

  const published = state?.published && state?.version;

  function update(index, patch) {
    setItems((list) => list.map((it, i) => (i === index ? { ...it, ...patch } : it)));
    setDirty(true);
  }

  function move(index, delta) {
    setItems((list) => {
      const next = [...list];
      const target = index + delta;
      if (target < 0 || target >= next.length) return list;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setDirty(true);
  }

  function remove(index) {
    setItems((list) => list.filter((_, i) => i !== index));
    setEditing(null);
    setDirty(true);
  }

  function add(type) {
    setItems((list) => [...list, blank(type)]);
    setEditing(items.length);
    setDirty(true);
  }

  async function save() {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const payload = items.map(({ _key, position, ...it }) => ({
        ...it,
        stem: it.stem.trim(),
        options: it.type === 'true_false' || it.type === 'free_text' ? [] : it.options.map((o) => o.trim()).filter(Boolean),
        correct:
          it.type === 'sequencing'
            ? null // the typed order is the correct order
            : it.type === 'free_text'
              ? null
              : it.correct || null,
        rationale: it.rationale?.trim() || null,
      }));
      const data = await apiFetch(`/api/v1/classes/${classId}/questions`, {
        method: 'PUT',
        body: JSON.stringify({ items: payload }),
      });
      setState(data);
      setItems((data.items ?? []).map((i) => ({ ...i, _key: i.id })));
      setDirty(false);
      setEditing(null);
      setNotice(`Saved ${data.items.length} item${data.items.length === 1 ? '' : 's'} on version ${data.version}.`);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const checks = items.filter((i) => i.is_completion_check).length;

  return (
    <section className="panel" aria-labelledby="items-heading">
      <h2 id="items-heading">Interaction items</h2>
      <ErrorBanner error={error} />
      {notice && (
        <p className="banner banner--success" role="status">
          {notice}
        </p>
      )}

      <p className="muted">
        What the room is asked during the session, in order — polls, true/false, order-of-priority,
        open questions. Mark the end-of-session assessment as the <strong>completion check</strong>:
        anyone who does not answer it is proposed <em>incomplete</em>, whatever their seat time.
      </p>

      {state === undefined && (
        <p className="muted" aria-busy="true">
          Loading…
        </p>
      )}

      {state && !published && (
        <p className="callout callout--warn">
          <strong>Publish the section first.</strong>
          <span className="callout__detail">
            Items are attached to a published version, so a session is always asked exactly the
            items frozen with the version it ran against.
          </span>
        </p>
      )}

      {published && (
        <>
          {items.length === 0 && <p className="muted">No items yet. Add the first one below.</p>}

          {items.length > 0 && checks === 0 && (
            <p className="callout callout--warn">
              <strong>No completion check yet.</strong>
              <span className="callout__detail">
                Without one, completion is proposed from seat time alone — someone who leaves at the
                break would still be proposed complete. Tick “completion check” on the final
                assessment item(s).
              </span>
            </p>
          )}

          <ol className="qlist">
            {items.map((it, index) => (
              <li key={it._key} className="qlist__item">
                {editing === index ? (
                  <ItemEditor
                    item={it}
                    onChange={(patch) => update(index, patch)}
                    onDone={() => setEditing(null)}
                    onRemove={() => remove(index)}
                    disabled={busy}
                  />
                ) : (
                  <div className="schedule__actions" style={{ alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div className="qlist__stem">
                        <span className="muted">{index + 1}. </span>
                        {it.stem || <em className="muted">(no wording yet)</em>}
                      </div>
                      <div className="muted">
                        {TYPE_LABEL[it.type]}
                        {it.type !== 'free_text' && it.type !== 'true_false' && ` · ${it.options.filter(Boolean).length} options`}
                        {it.type === 'true_false' && ` · answer: ${it.correct ?? '—'}`}
                        {it.type === 'multiple_choice' && it.correct && ` · answer: ${it.correct}`}
                        {it.is_completion_check && (
                          <>
                            {' '}
                            <span className="badge badge--published">completion check</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="schedule__actions">
                      <button type="button" className="btn btn--quiet btn--auto" disabled={busy || index === 0} onClick={() => move(index, -1)} aria-label={`Move item ${index + 1} up`}>
                        ↑
                      </button>
                      <button type="button" className="btn btn--quiet btn--auto" disabled={busy || index === items.length - 1} onClick={() => move(index, 1)} aria-label={`Move item ${index + 1} down`}>
                        ↓
                      </button>
                      <button type="button" className="btn btn--auto" disabled={busy} onClick={() => setEditing(index)}>
                        Edit
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ol>

          <div className="schedule__actions">
            {TYPE_OPTIONS.map((t) => (
              <button key={t.value} type="button" className="btn btn--auto" disabled={busy} onClick={() => add(t.value)}>
                + {t.label}
              </button>
            ))}
          </div>

          <div className="schedule__actions" style={{ marginTop: '1rem' }}>
            <button type="button" className="btn btn--primary btn--auto" disabled={busy || !dirty} onClick={save}>
              {busy ? 'Saving…' : dirty ? 'Save items' : 'Saved'}
            </button>
            {dirty && (
              <button type="button" className="btn btn--quiet btn--auto" disabled={busy} onClick={load}>
                Discard changes
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function ItemEditor({ item, onChange, onDone, onRemove, disabled }) {
  const hasOptions = item.type === 'multiple_choice' || item.type === 'sequencing';

  function setOption(i, value) {
    const options = item.options.map((o, j) => (j === i ? value : o));
    // Keep the correct answer pointing at the renamed option.
    const correct = item.type === 'multiple_choice' && item.correct === item.options[i] ? value : item.correct;
    onChange({ options, correct });
  }

  return (
    <div>
      <Select
        label="Type"
        value={item.type}
        onChange={(type) => onChange({ ...blank(type), id: item.id, stem: item.stem, rationale: item.rationale, is_completion_check: item.is_completion_check, _key: item._key })}
        options={TYPE_OPTIONS}
        placeholder="Choose…"
        disabled={disabled}
      />
      <Field label="Question" value={item.stem} onChange={(v) => onChange({ stem: v })} disabled={disabled} />

      {hasOptions && (
        <fieldset className="field">
          <legend>{item.type === 'sequencing' ? 'Options, in the correct order' : 'Options'}</legend>
          {item.options.map((o, i) => (
            <div key={i} className="schedule__actions">
              {item.type === 'multiple_choice' && (
                <label className="checkbox">
                  <input type="radio" name={`correct-${item._key}`} checked={item.correct === o && o !== ''} onChange={() => onChange({ correct: o })} disabled={disabled || !o} />
                  <span className="visually-hidden">Correct answer</span>
                </label>
              )}
              {item.type === 'sequencing' && <span className="muted">{i + 1}.</span>}
              <input type="text" value={o} onChange={(e) => setOption(i, e.target.value)} disabled={disabled} aria-label={`Option ${i + 1}`} style={{ flex: 1 }} />
              <button type="button" className="btn btn--quiet btn--auto" disabled={disabled || item.options.length <= 2} onClick={() => onChange({ options: item.options.filter((_, j) => j !== i), correct: item.correct === o ? null : item.correct })} aria-label={`Remove option ${i + 1}`}>
                ×
              </button>
            </div>
          ))}
          <button type="button" className="btn btn--quiet btn--auto" disabled={disabled || item.options.length >= 12} onClick={() => onChange({ options: [...item.options, ''] })}>
            + option
          </button>
          {item.type === 'multiple_choice' && <p className="field__hint">Select the radio next to the correct answer. Leave all unselected for a poll with no right answer.</p>}
        </fieldset>
      )}

      {item.type === 'true_false' && (
        <Select label="Correct answer" value={item.correct ?? ''} onChange={(v) => onChange({ correct: v || null })} options={[{ value: 'True', label: 'True' }, { value: 'False', label: 'False' }]} placeholder="No right answer (a poll)" required={false} optional={false} disabled={disabled} />
      )}

      <Field label="Rationale" value={item.rationale ?? ''} onChange={(v) => onChange({ rationale: v })} required={false} hint="Shown to staff only. Why the answer is what it is." disabled={disabled} />

      <label className="checkbox">
        <input type="checkbox" checked={item.is_completion_check} onChange={(e) => onChange({ is_completion_check: e.target.checked })} disabled={disabled} />
        <span>
          <strong>Completion check</strong> — part of the end-of-session assessment. Not answering it means no credit.
        </span>
      </label>

      <div className="schedule__actions">
        <button type="button" className="btn btn--primary btn--auto" disabled={disabled} onClick={onDone}>
          Done
        </button>
        <button type="button" className="btn btn--quiet btn--auto" disabled={disabled} onClick={onRemove}>
          Remove this item
        </button>
      </div>
    </div>
  );
}

export default SectionItemsPanel;
